import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { TariffSchedule, TariffRate } from '../api/types';
import MeterTypeIcon from '../components/MeterTypeIcon';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { DataTableColumn } from '../components/ui';
import { fmtCurrency } from '../lib/fmt';
import { chargeTypeName } from '../lib/chargeType';

export default function Tariffs() {
  const [tariffs, setTariffs] = useState<TariffSchedule[]>([]);
  const [selectedTariff, setSelectedTariff] = useState<TariffSchedule | null>(null);
  const [tariffRates, setTariffRates] = useState<TariffRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRates, setLoadingRates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Type-to-confirm delete modal — guards against accidental clicks on the
  // small "Delete" button next to similarly-named tariffs.
  const [deleteTarget, setDeleteTarget] = useState<TariffSchedule | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTariffs();
  }, []);

  const loadTariffs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getTariffs();
      setTariffs(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tariffs');
    } finally {
      setLoading(false);
    }
  };

  const loadTariffRates = async (tariffId: string) => {
    try {
      setLoadingRates(true);
      const res = await api.getTariffRates(tariffId);
      setTariffRates(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rates');
    } finally {
      setLoadingRates(false);
    }
  };

  const handleSelectTariff = async (tariff: TariffSchedule) => {
    if (selectedTariff?.id === tariff.id) {
      setSelectedTariff(null);
      setTariffRates([]);
    } else {
      setSelectedTariff(tariff);
      await loadTariffRates(tariff.id);
    }
  };

  // Opens the type-to-confirm modal. The actual API call is in confirmDelete
  // — the user must type the exact tariff name before the destructive button
  // enables, so a stray click can't take out the wrong tariff in a list of
  // similarly-named entries (e.g. "Eskom Landrate 1" vs "Eskom Landrate 2").
  const openDelete = (tariff: TariffSchedule) => {
    setDeleteTarget(tariff);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== deleteTarget.name) return;
    try {
      setDeleting(true);
      setError(null);
      await api.deleteTariff(deleteTarget.id);
      if (selectedTariff?.id === deleteTarget.id) {
        setSelectedTariff(null);
        setTariffRates([]);
      }
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await loadTariffs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tariff');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Converged onto the shared formatter (src/lib/fmt.ts) — see the
  // non-breaking-separator note there. Tariff RATES display at up to 4
  // decimals (e.g. R3,2413/kWh), hence the explicit max.
  const formatCurrency = (cents: number) => fmtCurrency(cents, 4);

  // 'tou' -> success (teal): same hue this pill already used (bg-primary-100
  // text-primary-800, and primary is teal), so this is a shade normalization
  // (800->900) via Badge, not a hue change — same class of adjustment
  // BillRuns.tsx's STATUS_TONE already made when it adopted Badge.
  const getRateTypeBadge = (type: string) => (
    <Badge tone={type === 'tou' ? 'success' : 'neutral'}>
      {type === 'tou' ? 'Time-of-Use' : 'Simple'}
    </Badge>
  );

  // peak/off_peak/standard map onto Badge's danger/success/warning tones —
  // the same red/(green->teal)/(yellow->amber) hue adjustments BillRuns.tsx's
  // STATUS_TONE already established for its 'error'/'approved'/
  // 'pending_approval' states when it adopted Badge.
  const TOU_TONE: Record<string, 'danger' | 'warning' | 'success' | 'neutral'> = {
    peak: 'danger',
    standard: 'warning',
    off_peak: 'success',
  };
  const getTOUBadge = (bucket: string) => (
    <Badge tone={TOU_TONE[bucket] ?? 'neutral'}>{bucket.replace('_', ' ')}</Badge>
  );

  // The local map that used to live here omitted `water_kl`, so a water
  // tariff's schedule rendered the raw key, "water kl". It now shares one
  // table with TariffManagement.tsx (src/lib/chargeType.ts) — the divergence
  // between the two copies was exactly how the gap went unnoticed.
  // `chargeTypeName` is the unit-free register, correct here because this
  // table has its own Unit column (:164).

  // Charge Type identifies the row; Rate is the number a reader of a rate
  // schedule actually scans for. TOU Period is a Badge (never primary — see
  // DataTable's collapsing-branch note: no overflow-x-auto fallback below
  // lg), so it stays secondary with Unit/Description. No selection or
  // rowActions on this read-only schedule, but 2 primaries already tell the
  // whole story (what it costs), so this doesn't stretch to the 3-primary
  // ceiling that would otherwise be available.
  const rateColumns: DataTableColumn<TariffRate>[] = [
    {
      key: 'charge_type',
      label: 'Charge Type',
      priority: 'primary',
      render: (rate) => <span className="font-medium text-content">{chargeTypeName(rate.charge_type)}</span>,
    },
    {
      key: 'tou_period',
      label: 'TOU Period',
      priority: 'secondary',
      render: (rate) => getTOUBadge(rate.tou_bucket),
    },
    {
      key: 'rate',
      label: 'Rate',
      priority: 'primary',
      numeric: true,
      render: (rate) => <span className="font-mono text-content">{formatCurrency(rate.rate_cents)}</span>,
    },
    {
      key: 'unit',
      label: 'Unit',
      priority: 'secondary',
      render: (rate) => <span className="text-content-muted">{rate.unit}</span>,
    },
    {
      key: 'description',
      label: 'Description',
      priority: 'secondary',
      render: (rate) => (
        <span className="text-sm text-content-muted">
          {rate.description}
          {rate.threshold_value && (
            <span className="ml-2 text-xs text-content-muted">
              (threshold: {rate.threshold_value}%)
            </span>
          )}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-border-token rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  // Utility-type derived for fallback when API doesn't include the field.
  const utilityOf = (t: TariffSchedule): 'water' | 'electricity' =>
    t.utility_type ?? (/water/i.test(t.name) ? 'water' : 'electricity');

  const electricityTariffs = tariffs.filter((t) => utilityOf(t) === 'electricity');
  const waterTariffs = tariffs.filter((t) => utilityOf(t) === 'water');

  const renderTariffCard = (tariff: TariffSchedule) => (
    <Card key={tariff.id} className="shadow-sm overflow-hidden">
      <button
        onClick={() => handleSelectTariff(tariff)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-sunken transition-colors"
      >
        <div className="text-left flex items-start gap-3">
          <MeterTypeIcon
            type={utilityOf(tariff) === 'water' ? 'GroundwaterSensorAsset' : 'ElectricityConsumerAsset'}
            className="w-6 h-6 flex-shrink-0 mt-0.5"
          />
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-content">{tariff.name}</h3>
              {getRateTypeBadge(tariff.rate_type)}
              {!tariff.is_active && (
                <Badge tone="danger">Inactive</Badge>
              )}
            </div>
            <p className="text-sm text-content-muted mt-1">{tariff.description}</p>
            <p className="text-xs text-content-muted mt-1">
              {tariff.municipality_name && `${tariff.municipality_name} | `}
              Effective from {formatDate(tariff.effective_from)}
              {tariff.effective_to && ` to ${formatDate(tariff.effective_to)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tariff.is_active && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); openDelete(tariff); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); openDelete(tariff); } }}
              className="text-xs px-2 py-1 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 cursor-pointer"
              title="Deactivate this tariff"
            >
              Delete
            </span>
          )}
          <svg
            className={`w-5 h-5 text-content-muted transition-transform ${
              selectedTariff?.id === tariff.id ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {selectedTariff?.id === tariff.id && renderRatesPanel()}
    </Card>
  );

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-content">Tariff Schedules</h1>
      </div>

      {/* Error */}
      {error && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {tariffs.length === 0 ? (
        <Card className="shadow-sm p-12 text-center text-content-muted">
          <p>No tariffs configured</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {electricityTariffs.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MeterTypeIcon type="ElectricityConsumerAsset" className="w-5 h-5" />
                <h2 className="text-lg font-semibold text-content">Electricity Tariffs</h2>
                <span className="text-sm text-content-muted">({electricityTariffs.length})</span>
              </div>
              <div className="space-y-3">{electricityTariffs.map(renderTariffCard)}</div>
            </section>
          )}
          {waterTariffs.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MeterTypeIcon type="GroundwaterSensorAsset" className="w-5 h-5" />
                <h2 className="text-lg font-semibold text-content">Water Tariffs</h2>
                <span className="text-sm text-content-muted">({waterTariffs.length})</span>
              </div>
              <div className="space-y-3">{waterTariffs.map(renderTariffCard)}</div>
            </section>
          )}
        </div>
      )}

      {/* Type-to-confirm delete modal. Button stays disabled until the typed
          text matches the tariff name exactly (case-sensitive) so similarly
          named tariffs in a list ("Landrate 1" / "Landrate 2") can't be
          confused. */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-border-token">
              <h3 className="text-lg font-semibold text-content">Deactivate tariff</h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <p className="text-content">
                You're about to deactivate{' '}
                <span className="font-semibold text-content">{deleteTarget.name}</span>.
                It will be hidden from picker dropdowns. Meters still assigned will keep
                the link until you re-assign them.
              </p>
              <p className="text-content-muted">
                To confirm, type the tariff name exactly:
                <span className="ml-1 font-mono text-content">{deleteTarget.name}</span>
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type tariff name here"
                autoFocus
                className="w-full px-3 py-2 border border-border-token rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="px-6 py-4 border-t border-border-token flex justify-end gap-2">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                disabled={deleting}
                className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText.trim() !== deleteTarget.name}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderRatesPanel() {
    return (
      <div className="border-t border-border-token">
        {loadingRates ? (
          <div className="p-6">
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 bg-border-token rounded"></div>
              ))}
            </div>
          </div>
        ) : (
          <DataTable
            data={tariffRates}
            columns={rateColumns}
            rowKey={(rate) => rate.id}
            emptyState={<p>No rates configured for this tariff</p>}
          />
        )}
      </div>
    );
  }
}
